import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { Calendar, ShieldCheck, BadgePercent } from "lucide-react";
import { Button } from "@/components/ui/button";
import PricingCards from "@/components/site/PricingCards";
import { useBookDemo } from "@/context/BookDemoContext";

export default function Priser() {
    const { open: openDemo } = useBookDemo();
    const builderRef = useRef(null);
    const location = useLocation();

    const scrollToBuilder = () => {
        if (builderRef.current) {
            builderRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
        }
    };

    // If user navigated here with #builder (eg from forsiden), scroll into view once mounted
    useEffect(() => {
        if (location.hash === "#builder") {
            // Wait a tick for layout
            const t = setTimeout(scrollToBuilder, 100);
            return () => clearTimeout(t);
        }
    }, [location.hash]);

    return (
        <div data-testid="page-priser">
            <section className="relative overflow-hidden">
                <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
                <div className="absolute -top-20 -right-32 w-[420px] h-[420px] rounded-full bg-gradient-to-br from-blue-200/40 via-indigo-200/40 to-purple-200/30 blur-3xl pointer-events-none" />

                <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-10 text-center">
                    <span className="inline-flex items-center px-3 py-1 rounded-full bg-blue-50 text-blue-700 text-[11px] font-semibold uppercase tracking-wider border border-blue-100">
                        Priser
                    </span>
                    <h1 className="mt-5 text-4xl sm:text-5xl lg:text-[3.25rem] font-bold tracking-tight text-slate-900 leading-[1.05]">
                        Enkle priser uden{" "}
                        <span className="gradient-text">unødigt rod.</span>
                    </h1>
                    <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl mx-auto">
                        Ét samlet abonnement med alt kernesystemet — uden binding.
                    </p>
                </div>
            </section>

            {/* Pricing */}
            <section className="pb-16" id="builder" ref={builderRef}>
                <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
                    <PricingCards onSelectPlan={scrollToBuilder} />
                </div>
            </section>

            <section className="pb-16">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div data-testid="trust-card-0" className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4">
                            <span className="shrink-0 w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 ring-1 ring-emerald-100 inline-flex items-center justify-center">
                                <ShieldCheck className="w-4 h-4" />
                            </span>
                            <div>
                                <p className="font-semibold text-slate-900">Gratis opsætning</p>
                                <p className="text-sm text-slate-600 mt-0.5">Vi sætter jer i gang uden ekstra omkostninger.</p>
                            </div>
                        </div>
                        <div data-testid="trust-card-1" className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4">
                            <span className="shrink-0 w-10 h-10 rounded-xl bg-blue-50 text-blue-600 ring-1 ring-blue-100 inline-flex items-center justify-center">
                                <BadgePercent className="w-4 h-4" />
                            </span>
                            <div>
                                <p className="font-semibold text-slate-900">Skaler op når I vokser</p>
                                <p className="text-sm text-slate-600 mt-0.5">Tilkøb ekstra flows og kapacitet pr. måned uden binding.</p>
                            </div>
                        </div>
                        <div data-testid="trust-card-2" className="rounded-2xl bg-white border border-slate-200 p-5 flex items-start gap-4">
                            <span className="shrink-0 w-10 h-10 rounded-xl bg-purple-50 text-purple-600 ring-1 ring-purple-100 inline-flex items-center justify-center">
                                <Calendar className="w-4 h-4" />
                            </span>
                            <div>
                                <p className="font-semibold text-slate-900">Demo først</p>
                                <p className="text-sm text-slate-600 mt-0.5">Vi viser flowet i praksis, før I beslutter jer.</p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA */}
            <section className="pb-20">
                <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <h3 className="text-2xl sm:text-3xl font-bold text-slate-900">Klar til at komme i gang?</h3>
                    <p className="mt-3 text-slate-600 max-w-xl mx-auto">
                        Book en gratis demo, så finder vi det rigtige setup sammen.
                    </p>
                    <div className="mt-5">
                        <Button data-testid="priser-cta-btn" onClick={() => openDemo()} className="rounded-full h-12 px-6 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white shadow-md">
                            <Calendar className="w-4 h-4 mr-2" />
                            Book gratis demo
                        </Button>
                    </div>
                </div>
            </section>
        </div>
    );
}
